// components/PrivacyPolicy.js
import React from 'react';
import styles from './PrivacyPolicy.module.css';
import PrivacyPolicyStyles from "src/pages/privacy-policy/PrivacyPolicyStyles";

const PrivacyPolicy = () => {
  return (
    <PrivacyPolicyStyles>
      <header>
        <h1>Privacy Policy</h1>
        <p>Last updated on 19 March 2025.</p>
      </header>

      <section>
        <h2>SCOPE OF THIS POLICY</h2>
        <p>
          CY CamFines Bot (“we”, “us”, “our”, “CamFines Bot”, “the Bot”) is committed to protecting your privacy. On this page, you can learn what information about you we collect while you interact with the Bot and how we process the personal data you provide us with.
        </p>
        <p>This Policy is intended to help you understand:</p>
        <ul>
          <li>why we collect your personal data;</li>
          <li>how we collect, use and store your personal data;</li>
          <li>which rights relating to your personal data you have;</li>
          <li>how you can exercise the rights relating to your personal data;</li>
          <li>how we use cookies and other tracking technologies;</li>
          <li>how we share and disclose your personal data.</li>
        </ul>
        <p>
          This Privacy Policy (“Policy”) applies between you and CamFines Bot. It describes how we handle the data you provide us with through our chatbot <a href="https://t.me/cy_camfines_bot">https://t.me/cy_camfines_bot</a>, or otherwise provide us with information about yourself.
        </p>
        <p>
          We collect personal data on your behalf from third parties (“Third-Party Sources”), which include Road Transport Department of the Republic of Cyprus, available under <a href="https://rtd.mcw.gov.cy/">https://rtd.mcw.gov.cy/</a>, Cyprus Camera System, available under <a href="https://cycamerasystem.com.cy/">https://cycamerasystem.com.cy/</a>, and JCC Smart platform in collaboration with Cyprus Police, available by the following link <a href="https://www.jccsmart.com/">https://www.jccsmart.com/</a>. Please note that we are not liable for how these third parties collect and process your personal data. To learn more about Third-Party Sources’ handling of your personal data, we recommend you to consult their privacy documents.
        </p>
        <p>
          When processing your personal data, CamFines Bot acts as a data controller under the GDPR.
        </p>
        <p>
          When you submit your personal data through the Bot’s interface while using our services, you are a User.
        </p>
      </section>

      <section>
        <h2>INTERPRETATION AND DEFINITIONS</h2>
        <p>We use the following definitions in this Policy:</p>
        <ul>
          <li><strong>“GDPR”</strong> means the General Data Protection Regulation (Regulation (EU) 2016/679).</li>
          <li><strong>“data controller”</strong> means the natural or legal person who (either alone or jointly or in common with other persons) determines the purposes for which and how any personal data is processed.</li>
          <li><strong>“data subject”</strong> is a person who can be identified, directly or indirectly, by details like their name, identification number, location, online identifier, or factors related to their physical, physiological, genetic, mental, economic, cultural or social identity.</li>
          <li><strong>“personal data”</strong> means any information relating to you and helping identify you (directly or indirectly), such as your name, last name, email, location, etc.</li>
          <li><strong>“processing”</strong> means any operation or set of operations which is performed on personal data or on sets of personal data, whether or not by automated means, such as collection, recording, organisation, structuring, storage, adaptation or alteration, retrieval, consultation, use, disclosure by transmission, dissemination or otherwise making available, alignment or combination, restriction, erasure or destruction.</li>
        </ul>
      </section>

      <section>
        <h2>TYPES OF PERSONAL DATA WE COLLECT</h2>
        <p>We collect and process information about you in accordance with this Policy. We may collect your personal data through the Bot, Third-Party Sources, email, or via other ways of communication.</p>
        <p>In particular, we collect the following:</p>
        <ol type="a">
          <li><strong>Identification information.</strong> When you register with the Bot to start using the services, we may collect your Alien Registration Number (ARC), passport number, company identification number, or Cypriot ID number. The Bot may retrieve information about your name and the payment of your road tax from Third-Party Sources.</li>
          <li><strong>Vehicle Information.</strong> When using the Bot, you may submit a vehicle plate number. When instructed by you, the Bot retrieves from Third-Party Services information about the availability of your vehicle insurance and conduction of a MOT test.</li>
          <li><strong>Fine information.</strong> Under your instruction, we may retrieve information about fine ID, fine amount, date of violation and penalty points from Third-Party Services.</li>
          <li><strong>Communication Information.</strong> We store the information about your communications with the Bot, such as the content of your messages to the Bot and your actions within the Bot.</li>
          <li><strong>Automatically collected information.</strong> The Bot may automatically collect information such as username to identify you as a user in its system and connect your personal data. For other types of automatically collected information, please consult Telegram Privacy Policy.</li>
        </ol>
        <p>
          We use the personal data we collect and process only for the purposes listed in this Policy. Telegram may occasionally present you with marketing banners related to the thematic purpose of the Bot. The display of such advertisements is not based on your personal data and is governed by Telegram Privacy Policy.
        </p>
        <p>
          We follow all privacy guidelines of Telegram, including Standard Bot Privacy Policy. By using our service, you confirm that you agree with the terms set forth in the abovementioned policy and that the information you provide us with is accurate and up-to-date.
        </p>
        <p><strong>We DO NOT sell your data.</strong></p>
        <p><strong>We DO NOT use automated decision-making, including profiling, which produces legal effects concerning a data subject or similarly significantly affects a data subject.</strong></p>
        <p><strong>We DO NOT intentionally collect and process the personal data of children and any sensitive personal data. Please, refrain from sharing your or third-party sensitive personal data.</strong></p>
      </section>

      <section>
        <h2>GROUNDS FOR PROCESSING</h2>
        <p>We collect and process your personal data in accordance with the provisions of the GDPR and other applicable laws and regulations.</p>
        <p>Under the GDPR there is an exclusive list of lawful bases, allowing us to process your personal data. During personal data processing, we rely only on two of them, namely:</p>
        <ul>
          <li><strong>Article 6.1(a): consent</strong><br />
            We collect the information you choose to give us, and we process it under your consent. You may withdraw your consent to the processing of your personal data at any time.<br />
            You may withdraw your consent to the processing of your personal data by entering the “/data” command and choosing the “Delete all personal data” option in the Bot.
          </li>
          <li><strong>Article 6.1(b): performance of a contract</strong><br />
            When you provide us with personal data via available options on the Bot, this can sometimes be considered a request to form a contract or perform a contract between you and us. However, we may ask you for clear consent in case of doubt.
          </li>
        </ul>
        <p>We only collect and use the strictly necessary data to achieve these purposes provided that your fundamental rights and freedoms are not overridden.</p>
      </section>

      <section>
        <h2>HOW WE USE YOUR DATA</h2>
        <p>When acting as a data controller, we use your personal data for the purposes listed in the table below, where we also detail the types of personal data processed, legal bases we rely on to do so, and information on the source of such data:</p>
        <table className={styles.table}>
          <thead>
          <tr>
            <th>Purpose of Processing</th>
            <th>Types of Personal Data</th>
            <th>Legal Grounds</th>
            <th>Source</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Providing User with information about the tax number, vehicle insurance and MOT</td>
            <td>(a) Identification information<br />(b) Vehicle Information</td>
            <td>Your consent (Article 6(1)(a))<br />Performance of a contract (Article 6(1)(b))</td>
            <td>User, Third-Party Sources</td>
          </tr>
          <tr>
            <td>Providing User with information about fine ID</td>
            <td>(a) Identification information<br />(b) Vehicle Information</td>
            <td>Your consent (Article 6(1)(a))<br />Performance of a contract (Article 6(1)(b))</td>
            <td>User, Third-Party Sources</td>
          </tr>
          <tr>
            <td>Providing User with information about fine amount, date of violation and penalty points</td>
            <td>(b) Vehicle information<br />(c) Fine information</td>
            <td>Your consent (Article 6(1)(a))<br />Performance of a contract (Article 6(1)(b))</td>
            <td>User, Third-Party Sources</td>
          </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>SHARING YOUR PERSONAL DATA WITH OTHER ENTITIES</h2>
        <p>
          We use Digital Ocean (Digital Ocean, LLC, USA) for hosting purposes. Therefore, we may share and disclose your personal data to this company, who is acting as our data processor. We do so under the terms of the Data Processing Agreement between us and Digital Ocean, which you may find <a href="#">here</a>.
        </p>
      </section>

      <section>
        <h2>DATA RETENTION</h2>
        <p>
          We keep personal data for a duration of your use of the Bot and for a period of 14 days after deletion, after which the personal data is erased.
        </p>
        <p>
          We may not delete or anonymise your data if we are compelled to keep it under the GDPR and other applicable laws.
        </p>
        <p>
          Notwithstanding any of the aforementioned periods of data storage, you may request to delete your personal data by entering the “/data” command into the Bot and choosing the “Delete all personal data” option.
        </p>
      </section>

      <section>
        <h2>SECURITY AND INTEGRITY OF THE DATA</h2>
        <p>
          We have implemented appropriate organisational, technical, administrative, and physical security measures designed to protect your personal data from unauthorised access, disclosure, use, and modification. We regularly review our security procedures to consider appropriate new technology and methods.
        </p>
      </section>

      <section>
        <h2>LINKS TO THIRD-PARTY WEBSITES OR SERVICES</h2>
        <p>
          This Policy applies only to this Bot. We strongly recommend you review the privacy documents of any websites you may reach by following the hyperlinks provided by our Bot. We have no control over the content and data practices of other websites and are not responsible for their actions.
        </p>
      </section>

      <section>
        <h2>DATA SUBJECT AGE</h2>
        <p>
          Our Bot and services are intended for general audiences and are not directed to children under the age of 18. By submitting your personal data to us, you acknowledge that you have reached the age of 18, and under the laws of your country of residence, you have all rights to provide us with your personal data for processing.
        </p>
        <p>
          Under the GDPR, we do not knowingly collect any personal data from children under age of sixteen (or a lower age if provided by EU member state law, provided that such lower age is not below 13 years).
        </p>
        <p>
          If we learn we have collected or received personal data from a child, we will delete that information. If you have any reason to believe that a child has provided their personal data to us, please contact us at <a href="mailto:cycamfinesbot@gmail.com">cycamfinesbot@gmail.com</a>.
        </p>
      </section>

      <section>
        <h2>RIGHTS UNDER GDPR</h2>
        <p>
          You may exercise the following rights by submitting a data subject request at <a href="mailto:cycamfinesbot@gmail.com">cycamfinesbot@gmail.com</a>.
        </p>
        <p>
          Please note that we may need to confirm your identity to process your requests to exercise your rights under the GDPR. Thus, we may not be able to satisfy your request if you do not provide us with sufficient detail to allow us to verify your identity and respond to your request.
        </p>
        <p>
          For rights and obligations under Standard Bot Privacy Policy, you may consult the <a href="#">link</a>.
        </p>
        <table className={styles.table}>
          <thead>
          <tr>
            <th>Right under the GDPR</th>
            <th>Description</th>
            <th>How to exercise it</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Right to withdraw consent (Art. 7)</td>
            <td>You can withdraw your consent for data processing at any time.</td>
            <td>You can withdraw your consent by deleting your personal data within the Bot through the “/data” command and the “Delete all personal data” option.</td>
          </tr>
          <tr>
            <td>Right to be informed (Art. 13, 14)</td>
            <td>You have the right to be informed about the collection and use of your personal data.</td>
            <td>All information about our collection and use of your personal data is described in this Privacy Policy.</td>
          </tr>
          <tr>
            <td>Right of access (Art. 15)</td>
            <td>You have the right to confirm whether your personal data is being processed by us and access such data, along with specific information.</td>
            <td>You may find out which personal data is processed by us by entering the “/data” command into the Bot and choosing the “Download all personal data” option or submit a request.</td>
          </tr>
          <tr>
            <td>Right to rectification (Art. 16)</td>
            <td>You have the right to correct inaccurate personal data about you and to have incomplete personal data completed.</td>
            <td>You can submit a request.</td>
          </tr>
          <tr>
            <td>Right to erasure (“right to be forgotten”) (Art. 17)</td>
            <td>You have the right to have your personal data deleted without undue delay where one of the following grounds applies: the personal data are no longer necessary in relation to the purposes for which they were collected or otherwise processed; you withdraw consent to consent-based processing; you object to the processing under certain rules of applicable data protection law; the personal data have to be erased for compliance with a legal obligation in the European Union or an EU Member State law; the personal data have been collected in relation to the offer of information society services referred to in Article 8(1); the personal data have been unlawfully processed.</td>
            <td>You may request to delete your personal data by entering the “/data” command and choosing the “Delete all personal data” option in the Bot.</td>
          </tr>
          <tr>
            <td>Right to restriction of processing (Art. 18)</td>
            <td>You can limit the way in which we use your data where one of the following applies: you contest the accuracy of the personal data; processing is unlawful, but you oppose erasure; we no longer need the personal data for the purposes of our processing, but you require personal data for the establishment, exercise, or defence of legal claims; you have objected to processing, pending the verification of that objection.</td>
            <td>You can submit a request.</td>
          </tr>
          <tr>
            <td>Right to data portability (Art. 20)</td>
            <td>You have the right to receive your personal data in a structured, commonly accepted, and machine-readable format and have the right to request that we transmit this data directly to another controller to the extent that the legal basis for our processing of your personal data is your consent or performance of a contract and the processing is carried out by automated means.</td>
            <td>You can submit a request.</td>
          </tr>
          <tr>
            <td>Right to object (Art. 21)</td>
            <td>You have the right to object to our processing of your personal data at any time to the extent that the processing is based on point (e) or (f) of Article 6(1), including profiling based on those provisions. Also, you have the right to object to our processing of your personal data for direct marketing purposes (including profiling).</td>
            <td>You can submit a request.</td>
          </tr>
          <tr>
            <td>Right not to be subject to a decision based solely on automated processing, including profiling (Art. 22)</td>
            <td>This right restricts us from making solely automated decisions, including those based on profiling, which produce legal or other significant effects for data subjects.</td>
            <td>We DO NOT use automated decision-making and profiling.</td>
          </tr>
          <tr>
            <td>Right to lodge a complaint (Art. 77)</td>
            <td>You have the right to lodge a complaint with the supervisory authority if you believe that the processing of your personal data violates the requirements of the GDPR.</td>
            <td>You can submit the complaint in the EU member state of your place of habitual residence or to the data protection authority stated in this Privacy Policy.</td>
          </tr>
          <tr>
            <td>Right to compensation (Art. 82)</td>
            <td>Any person who has suffered material or moral damage as a result of a violation of GDPR requirements has the right to receive compensation from the controller or processor for the caused damage.</td>
            <td>Court proceedings for exercising the right to receive compensation shall be brought before the courts competent under the law of the EU Member State referred to in Article 79(2).</td>
          </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>COMPLAINTS</h2>
        <p>
          We encourage you to reach out to us initially with any concerns you may have regarding the processing of your personal data. You may use the following email to address your inquiries: <a href="mailto:cycamfinesbot@gmail.com">cycamfinesbot@gmail.com</a>.
        </p>
        <p>
          You have the right to lodge a complaint about our use of your personal data with a data protection authority. We will cooperate with the appropriate governmental authorities to resolve any privacy-related complaints that cannot be amicably resolved between you and us.
        </p>
        <p>For more information, please contact Cypriot national data protection authority by this address:</p>
        <p>
          Commissioner for Personal Data Protection<br />
          15, Kypranoros Street<br />
          1061 Nicosia<br />
          P.O. Box. 23378<br />
          1682 Nicosia<br />
          Tel. +357 22 818 456<br />
          Fax +357 22 304 565<br />
          Email: <a href="mailto:commissioner@dataprotection.gov.cy">commissioner@dataprotection.gov.cy</a><br />
          Website: <a href="http://www.dataprotection.gov.cy/">http://www.dataprotection.gov.cy/</a>
        </p>
      </section>

      <section>
        <h2>AMENDMENTS TO THE POLICY</h2>
        <p>
          We may periodically update this Policy to reflect new updates, technologies, legal requirements, or for other reasons. Any changes will be communicated by posting an updated version of the Privacy Policy.
        </p>
        <p>
          We encourage you to review this Policy periodically. If possible, we always give advance notice of upcoming changes by indicating when the new version of the Privacy Policy will take effect. If you continue to use our Website or otherwise provide us with your personal data after the new version of the Privacy Policy goes into effect, we assume that you agree to the changes.
        </p>
      </section>

      <section>
        <h2>CONTACT DETAILS</h2>
        <p>
          The name of the controller: Volodymyr Duchenchuk
        </p>
        <p>
          If you have a question related to this Policy, our data processing activities, or your data subject rights under the GDPR and other applicable data protection laws, you can contact us using the following email: <a href="mailto:cycamfinesbot@gmail.com">cycamfinesbot@gmail.com</a>
        </p>
      </section>
    </PrivacyPolicyStyles>
  );
};

export default PrivacyPolicy;
